import React, { useEffect, useRef, useState, useCallback } from "react";
import { View, Text, TouchableOpacity, StyleSheet, useWindowDimensions, Platform } from "react-native";
import { ChevronLeft, Lock } from "lucide-react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { getScaledValue, isFactorTv } from "@rnv/renative";
import { ChildStackParamList } from "../../navigation/types";
import { useAuthStore } from "../../state/authStore";
import { useSessionStore } from "../../state/sessionStore";
import useKeydown from "../../hooks/useKeydown";
import { useSessionSocket } from "../../hooks/useSessionSocket";
import { contentApi, VideoDetail, Question } from "../../api/content";
import { ApiError } from "../../api/client";
import { sessionsApi, StopReason } from "../../api/sessions";
import VideoPlayer, { VideoPlayerHandle } from "../../components/VideoPlayer";
import QuestionOverlay from "../../components/QuestionOverlay";
import KidModeLock from "../../components/KidModeLock";
import VoiceBedtimeScreen from "./VoiceBedtimeScreen";

// Bedtime videos render the dark voice-only stage instead of the normal
// player. Match category case-insensitively because the data model is open
// (admin-edited strings).
function isBedtime(video: VideoDetail | null): boolean {
  if (!video) return false;
  return (video.categories ?? []).some(
    (c) => typeof c === "string" && c.toLowerCase() === "bedtime",
  );
}

type Props = NativeStackScreenProps<ChildStackParamList, "Player">;

// Self-heal for TVs paired before parentId was persisted in the auth
// store. Without a parentId the WebSocket never joins the parent room,
// so `session_started` / heartbeats never fan out to the parent web.
function decodeJwtSub(token: string | null): string | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = typeof atob === "function" ? atob(padded) : "";
    const payload = JSON.parse(decoded);
    return typeof payload.sub === "string" ? payload.sub : null;
  } catch {
    return null;
  }
}

export default function PlayerScreen({ navigation, route }: Props) {
  const { childId, videoId } = route.params;
  const { accessToken, parentId, setParentId, tvId, kidMode } = useAuthStore();
  const effectiveParentId = parentId ?? decodeJwtSub(accessToken);
  const insets = useSafeAreaInsets();
  const playerRef = useRef<VideoPlayerHandle>(null);
  // Lock the fullscreen player to live window size on web. flex:1 alone
  // collapses the loading view and the audio-only stage to ~0 height on
  // Tizen/webOS (their children are absolute/tiny, so the flex parent has
  // nothing to stretch it). Prefer the live hook value; fall back to
  // window.inner* because RN's Dimensions reports height 0 before first
  // layout on Tizen.
  const dims = useWindowDimensions();
  const winW = dims.width || (typeof window !== "undefined" ? window.innerWidth : 0);
  const winH = dims.height || (typeof window !== "undefined" ? window.innerHeight : 0);
  // Must neutralize flex too: RN-web compiles `flex:1` to `flex-basis:0%`,
  // which overrides an explicit height in the (0-height) Tizen flex chain.
  // flex 0/0/auto makes the explicit width/height the real box size, exactly
  // like PairingScreen's no-flex container.
  const fill = Platform.OS === "web"
    ? { width: winW, height: winH, flexGrow: 0, flexShrink: 0, flexBasis: "auto" as const }
    : null;

  useEffect(() => {
    if (!parentId && effectiveParentId) setParentId(effectiveParentId);
  }, [parentId, effectiveParentId, setParentId]);
  const { sessionId, setSession, updateRemaining, clearSession } =
    useSessionStore();
  const [video, setVideo] = useState<VideoDetail | null>(null);
  // Premium content is visible on mobile (lock badge in the catalog) but only
  // plays for subscribers. The backend is the source of truth: a 403
  // PREMIUM_REQUIRED on load flips this and we show the locked overlay instead
  // of spinning forever. (On TV premium is filtered out of the catalog
  // entirely, so this path normally only fires on phone/tablet.)
  const [paywalled, setPaywalled] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState<Question | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastHeartbeat = useRef(0);
  const elapsedRef = useRef(0);
  const sessionEndedRef = useRef(false);

  useEffect(() => {
    if (!accessToken) return;
    contentApi
      .get(videoId, accessToken)
      .then(setVideo)
      .catch((e: unknown) => {
        // Premium-gated for this caller → show the locked overlay. Match by
        // status OR the PREMIUM_REQUIRED contract string (mirrors WebPlayer).
        if (
          (e instanceof ApiError && e.status === 403) ||
          (e instanceof Error && /PREMIUM_REQUIRED/.test(e.message))
        ) {
          setPaywalled(true);
        }
      });
    sessionsApi
      .start(childId, videoId, accessToken)
      .then(({ session, remainingSeconds }) => {
        setSession(session.id, videoId, remainingSeconds);
      });
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, []);

  // Soft-end the session and route to the right screen based on why we stopped.
  const endSession = useCallback(
    (reason: StopReason = null) => {
      if (sessionEndedRef.current) return;
      sessionEndedRef.current = true;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);

      const seconds = elapsedRef.current;
      const finalize = () => {
        clearSession();
        if (reason === "parent") {
          navigation.replace("BreakTime", { reason });
        } else if (reason === "daily" || reason === "session") {
          navigation.replace("BreakTime", { reason });
        } else {
          navigation.goBack();
        }
      };

      if (accessToken && sessionId) {
        sessionsApi.end(sessionId, seconds, accessToken).finally(finalize);
      } else {
        finalize();
      }
    },
    [accessToken, sessionId, clearSession, navigation],
  );

  // Real-time WebSocket: parent → TV remote commands.
  // `stop` ends the session immediately. The heartbeat failsafe catches it
  // anyway within ~10s if the socket drops.
  //
  // In mobile *kid mode* the parent IS the device — there's no other parent
  // to send events to or receive commands from. Pass `parentId: null` so
  // the socket never connects, which is simpler than gating every emit.
  const { emitTvEvent } = useSessionSocket({
    // Disable the socket only on a MOBILE kid-mode device: the parent IS
    // the device there, so there's nobody to send to. On TV (Tizen /
    // webOS / kid web), kidMode is also true by default but the parent
    // on a different phone/web tab IS watching — so the socket has to
    // stay connected for Co-watch + Now Playing to ever fill in.
    parentId:
      kidMode && !isFactorTv ? null : (effectiveParentId ?? null),
    role: "tv",
    // Multi-TV: pass tvId so the gateway routes parent_command to this
    // specific device's player socket (matches the always-on TvApp
    // socket's bucketing).
    tvId: tvId ?? null,
    onParentCommand: useCallback(
      (command) => {
        if (command.action === "pause") setPaused(true);
        else if (command.action === "resume") setPaused(false);
        else if (command.action === "stop") endSession("parent");
      },
      [endSession],
    ),
  });

  // REST heartbeat for daily/session limit enforcement.
  useEffect(() => {
    if (!sessionId || !accessToken) return;
    heartbeatRef.current = setInterval(() => {
      const secs = lastHeartbeat.current;
      if (secs > 0) {
        sessionsApi
          .heartbeat(sessionId, secs, accessToken)
          .then((r) => {
            updateRemaining(r.remainingDailySeconds);
            if (r.shouldStop) endSession(r.stopReason);
          })
          .catch(() => {});
      }
    }, 10000);
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [sessionId, accessToken, endSession, updateRemaining]);

  // Live WebSocket heartbeat so the parent's web Co-watch shows real
  // progress in near-realtime (every 2s). Lightweight: just numbers.
  useEffect(() => {
    if (!sessionId || !effectiveParentId || !video) return;
    const id = setInterval(() => {
      const secs = elapsedRef.current;
      const dur = video.duration ?? 0;
      emitTvEvent({
        type: "heartbeat",
        parentId: effectiveParentId,
        childId,
        data: {
          sessionId,
          secondsWatched: secs,
          progress: dur > 0 ? Math.min(1, secs / dur) : 0,
          paused,
        },
      });
    }, 2000);
    return () => clearInterval(id);
  }, [sessionId, effectiveParentId, childId, video, paused, emitTvEvent]);

  // Tell the parent's phone a session has started. They'll render the
  // Now Playing bar with a Stop button. On unmount, emit session_ended
  // so the bar clears.
  useEffect(() => {
    if (!sessionId || !effectiveParentId || !video) return;
    emitTvEvent({
      type: "session_started",
      parentId: effectiveParentId,
      childId,
      data: {
        sessionId,
        videoId,
        videoTitle: video.title,
        durationSec: video.duration,
        categories: video.categories,
      },
    });
    return () => {
      emitTvEvent({
        type: "session_ended",
        parentId: effectiveParentId,
        childId,
        data: { sessionId },
      });
    };
  }, [sessionId, effectiveParentId, childId, videoId, video, emitTvEvent]);

  // Broadcast question lifecycle so the parent sees what the child is being
  // asked. The web Co-watch surface renders this as a read-only overlay.
  useEffect(() => {
    if (!sessionId || !effectiveParentId) return;
    if (activeQuestion) {
      emitTvEvent({
        type: "question_shown",
        parentId: effectiveParentId,
        childId,
        data: {
          sessionId,
          question: {
            id: activeQuestion.id,
            prompt: activeQuestion.prompt,
            options: activeQuestion.options,
            correct: activeQuestion.correct,
          },
        },
      });
    }
  }, [activeQuestion, sessionId, effectiveParentId, childId, emitTvEvent]);

  const onAnswerSubmittedFromOverlay = useCallback(
    (questionId: string, selectedOption: number, correct: boolean) => {
      if (!sessionId || !effectiveParentId) return;
      emitTvEvent({
        type: "answer_submitted",
        parentId: effectiveParentId,
        childId,
        data: { sessionId, questionId, selectedOption, correct },
      });
    },
    [sessionId, effectiveParentId, childId, emitTvEvent],
  );

  const onQuestionDismissed = useCallback(() => {
    const q = activeQuestion;
    setActiveQuestion(null);
    if (q && sessionId && effectiveParentId) {
      emitTvEvent({
        type: "question_dismissed",
        parentId: effectiveParentId,
        childId,
        data: { sessionId, questionId: q.id },
      });
    }
  }, [activeQuestion, sessionId, effectiveParentId, childId, emitTvEvent]);

  const triggeredRef = useRef<Set<string>>(new Set());

  const handleTimeUpdate = (seconds: number) => {
    setElapsed(seconds);
    elapsedRef.current = seconds;
    lastHeartbeat.current = seconds;

    if (!video) return;
    // Bedtime is meant to wind kids down — no quiz interrupts.
    if (isBedtime(video)) return;
    // Backward seek: re-arm questions now in the future again so rewinding
    // past one lets it fire a second time (+2s hysteresis vs. normal ticking).
    for (const q of video.questions) {
      if (seconds + 2 < q.tSec) triggeredRef.current.delete(q.id);
    }
    if (activeQuestion) return;
    // Fire as soon as the playhead reaches a question's tSec — no upper window,
    // so seeking *past* a question still surfaces it. A forward seek over
    // several shows the latest reached and consumes the rest (no backlog).
    const reached = video.questions.filter(
      (q) => !triggeredRef.current.has(q.id) && seconds >= q.tSec,
    );
    if (reached.length > 0) {
      reached.sort((a, b) => a.tSec - b.tSec);
      const fire = reached[reached.length - 1];
      for (const q of reached) triggeredRef.current.add(q.id);
      setActiveQuestion(fire);
      // If the kid tapped the player's fullscreen button, the native
      // fullscreen view sits above the RN tree and hides the quiz. Exit it so
      // the QuestionOverlay is actually visible.
      playerRef.current?.dismissFullscreen();
    }
  };

  useKeydown({
    isActive: !activeQuestion,
    back: () => endSession(),
  });

  // Lock iOS swipe-back gesture in kid mode — exit must go through the
  // lock badge so the parent's PIN is the only way back to the parent app.
  // Backing out of Player to ChildHome via the back button stays fine since
  // ChildHome is still inside kid mode.
  useEffect(() => {
    navigation.setOptions({ gestureEnabled: !kidMode });
  }, [navigation, kidMode]);

  if (paywalled) {
    return (
      <View style={[styles.container, styles.centerFill, fill]}>
        <View style={styles.lockCircle}>
          <Lock size={getScaledValue(34)} color="#fff" strokeWidth={2.2} />
        </View>
        <Text style={styles.paywallTitle}>Kidly+ video</Text>
        <Text style={styles.paywallBody}>
          Ask a grown-up to unlock this with Kidly+.
        </Text>
        <TouchableOpacity
          style={styles.paywallBtn}
          onPress={() => endSession()}
        >
          <Text style={styles.paywallBtnText}>Go back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (!video) {
    return (
      <View style={[styles.container, styles.centerFill, fill]}>
        <Text style={styles.loading}>Loading…</Text>
      </View>
    );
  }

  const bedtime = isBedtime(video);

  return (
    <View style={[styles.container, fill]}>
      {bedtime ? (
        <VoiceBedtimeScreen
          video={video}
          elapsed={elapsed}
          paused={paused}
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => endSession()}
        />
      ) : (
        <VideoPlayer
          ref={playerRef}
          hlsUrl={video.hlsUrl}
          mimeType={video.videoMimeType}
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => endSession()}
          paused={!!activeQuestion || paused}
        />
      )}
      <TouchableOpacity
        style={[
          styles.backBtn,
          { top: insets.top + 8, left: insets.left + 12 },
        ]}
        onPress={() => endSession()}
      >
        <ChevronLeft size={20} color="#fff" strokeWidth={2.2} />
      </TouchableOpacity>
      {!bedtime && activeQuestion && sessionId && accessToken && (
        <QuestionOverlay
          question={activeQuestion}
          sessionId={sessionId}
          token={accessToken}
          onAnswered={onAnswerSubmittedFromOverlay}
          onClose={onQuestionDismissed}
        />
      )}
      {/* Tear down the running session when the parent exits kid mode so
          the dashboard doesn't show a stale "playing" state. We end the
          session via REST directly (not endSession()) so KidModeLock's own
          navigation.reset to ParentHome wins — endSession would race us
          to BreakTime instead. */}
      <KidModeLock
        onExit={() => {
          if (sessionId && accessToken && !sessionEndedRef.current) {
            sessionEndedRef.current = true;
            sessionsApi
              .end(sessionId, elapsedRef.current, accessToken)
              .catch(() => {});
            clearSession();
          }
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#000" },
  centerFill: { alignItems: "center", justifyContent: "center" },
  loading: {
    color: "#fff",
    fontSize: getScaledValue(20),
    textAlign: "center",
  },
  backBtn: {
    position: "absolute",
    top: getScaledValue(20),
    left: getScaledValue(20),
    padding: getScaledValue(10),
    backgroundColor: "rgba(0,0,0,0.6)",
    borderRadius: getScaledValue(8),
  },
  backText: { color: "#fff", fontSize: getScaledValue(16) },
  lockCircle: {
    width: getScaledValue(76),
    height: getScaledValue(76),
    borderRadius: getScaledValue(38),
    backgroundColor: "rgba(255,255,255,0.14)",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: getScaledValue(18),
  },
  paywallTitle: {
    color: "#fff",
    fontSize: getScaledValue(24),
    fontWeight: "800",
    textAlign: "center",
  },
  paywallBody: {
    color: "rgba(255,255,255,0.75)",
    fontSize: getScaledValue(15),
    textAlign: "center",
    marginTop: getScaledValue(8),
    paddingHorizontal: getScaledValue(32),
  },
  paywallBtn: {
    marginTop: getScaledValue(24),
    paddingVertical: getScaledValue(12),
    paddingHorizontal: getScaledValue(28),
    backgroundColor: "rgba(255,255,255,0.16)",
    borderRadius: getScaledValue(999),
  },
  paywallBtnText: {
    color: "#fff",
    fontSize: getScaledValue(16),
    fontWeight: "700",
  },
});
